import { uploadBytes } from 'firebase/storage';

import { getAppStorageFileRef, getHostingBucket } from 'services/Firebase';
import { computeCreativeStorageScope } from 'stores/CreativeUtils';
import { AssetFileType } from 'types/AssetFileType';
import { AssetSortOptions } from 'types/AssetSortOptions';
import { DocumentType } from 'types/DocumentType';
import { ProcessingStatus } from 'types/ProcessingStatus';
import { UploadStates } from 'types/UploadStates';
import { nowTimestamp, timestampCompare } from 'utils/FirestoreUtils';
import { numericCompare } from 'utils/Sort';
import { slashJoin } from 'utils/String';

import { getAdHubClientConfig } from '../config';
import type Animation from './Animation';
import Asset, { AssetSchema } from './Asset';
import AssetUploadManager from './AssetUploadManager';
import type Creative from './Creative';
import DocumentCollection from './DocumentCollection';
import FirebaseFunctions from './FirebaseFunctions';
import root, { type AppView } from './RootStore';

export interface AssetUploadOptions {
    postProcess?: boolean | null | undefined;
    overrideFileName?: string | null | undefined;
    checksum?: string | null | undefined;
}

function parentPath(path: string): string {
    const lastSlash = path.lastIndexOf('/');
    if (lastSlash === -1) return '';
    return path.slice(0, lastSlash);
}

function isImmediateChildOfFolder(parentPath: string, queryPath: string) {
    // root case: parentPath will be empty. any occurrence of slash means
    // queryPath is in some subfolder, and not the root. if a slash is
    // absent, it's in the root
    if (!parentPath) return !queryPath.includes('/');

    // not-root case. First check parentPath is in the ancestry at all
    if (!queryPath.startsWith(parentPath + '/')) return false;

    // if it is, any slashes after that point means it's a grandchild (or
    // further)
    return !queryPath.includes('/', parentPath.length + 1);
}

function ifViewHasAssets(view: AppView | undefined) {
    if (view) {
        switch (view.type) {
            case DocumentType.ANIMATION:
                return view as Animation;
            case DocumentType.CREATIVE:
                return view as Creative;
        }
    }
    return undefined;
}

class AssetsManager extends DocumentCollection<Asset, AssetSchema> {
    currentSortOption: AssetSortOptions = AssetSortOptions.NAME;
    sortDirection: 'asc' | 'desc' = 'desc';
    currentFolder = '';
    uploadState = UploadStates.NONE;
    baseURL = getHostingBucket().websiteOrigin;
    files: Asset[] = [];
    uploadManager = new AssetUploadManager(this);
    // age: NaN,

    constructor(parent: any){
        super(parent, Asset);
        this.becomeObservable();
    }

    get allReady() {
        return this.all.filter((item) => !item.processing);
    }

    get folderContext() {
        const list = this.getItemsImmediatelyInFolder(this.currentFolder);

        // Sort the list based on the current sort option
        list.sort((a, b) => {
            let comparison: number;
            switch (this.currentSortOption) {
                case AssetSortOptions.DATE_MODIFIED:
                    // original code had b as the first operand, making 'asc'
                    // actually descending. so date sorting is reversed wrt
                    // the other options
                    comparison = timestampCompare(
                        b.data.dateModified,
                        a.data.dateModified
                    );
                    break;
                case AssetSortOptions.NAME:
                default: // sort by name if somehow nothing selected
                    comparison = a.data.name.localeCompare(b.data.name);
                    break;
                case AssetSortOptions.SIZE:
                    comparison = numericCompare(a.fileSize, b.fileSize);
                    break;
            }
            return this.sortDirection === 'asc' ? comparison : -comparison;
        });

        return list;
    }

    getItemsImmediatelyInFolder = (folderPath: string) => {
        return this.all.filter((a) =>
            isImmediateChildOfFolder(folderPath, a.data.path)
        );
    };

    getItemsRecursivelyInFolder = (folderPath: string) => {
        return this.all.filter(
            (item) => item.data.path.indexOf(folderPath) === 0
        );
    };

    reload = async () => {
        await this.load();
    }

    setCurrentFolder = (folderPath: string) => {
        this.currentFolder = folderPath;
        this.files = this.folderContext;
    }

    navigateFolderUp = () => {
        this.currentFolder = parentPath(this.currentFolder);
    }

    uploadToCurrentAssetFolder = (
        userFile: File,
        options?: AssetUploadOptions
    ): Promise<Asset> => {
        return this.uploadToAssetFolder(userFile, this.currentFolder, options);
    };

    uploadToAssetFolder = async (
        userFile: File,
        assetFolder: string,
        options?: AssetUploadOptions
    ): Promise<Asset> => {
        const logPrefix = '[AssetsManager.uploadToAssetFolder]';
        const fileName = options?.overrideFileName || userFile.name; // can File.name be empty?
        const postProcess = !!options?.postProcess;
        const checksum = options?.checksum || '';

        console.log(
            `${logPrefix} userFile.name=${userFile.name} fileName=${fileName} postProcess=${postProcess} assetFolder=${assetFolder} checksum=${checksum}`
        );
        const creative = ifViewHasAssets(root.current);
        if (!creative) {
            throw new Error(
                'Assert: root.current.type is not ANIMATION or CREATIVE'
            );
        }

        this.uploadState = UploadStates.UPLOADING;

        const filePath = assetFolder
            ? slashJoin(assetFolder, fileName)
            : fileName;

        console.log(
            `${logPrefix} fileName=${fileName} filePath=${filePath} assetFolder=${assetFolder} checksum=${checksum}`
        );

        // Create all necessary folders in the path if needed
        if (assetFolder) {
            await this.newFolder(assetFolder);
        }

        const creativeScope = computeCreativeStorageScope(creative.path);
        const assetsStoragePath = getAdHubClientConfig().assetsStoragePath;
        // Unix millis prepended with no separator, so `animation.json`
        // becomes `1785565978745logo.png`. Not my first choice, but it does
        // the one job it needs to: re-uploading the same filename gets a
        // distinct storage path instead of overwriting. Prepending rather
        // than appending also sidesteps having to split the extension off,
        // which is fiddlier than it looks. Never surfaced to the user --
        // the asset's display name is `fileName` above, and this only forms
        // the Storage object path.
        const datePath = `${Date.now()}${filePath}`;
        const storagePath = slashJoin(
            assetsStoragePath,
            creativeScope,
            datePath
        );

        const fileRef = getAppStorageFileRef(storagePath);
        const existingAsset = this.getByPath(filePath);

        try {
            await uploadBytes(fileRef, userFile);
        } catch (error) {
            this.uploadState = UploadStates.UPLOAD_FAILED;
            if (existingAsset) {
                existingAsset.setLoading(false);
            }

            console.log(`${logPrefix} Upload failed:`, error);
            throw new Error('Assets Manager: Asset upload failed.');
        }

        const patch = {
            name: fileName,
            path: filePath,
            type: AssetFileType.FILE,
            fileType: AssetFileType.FILE,
            storage: storagePath,
            status: ProcessingStatus.NONE,
            dateModified: nowTimestamp(),
            checksum
        };

        let uploadedAsset: Asset;
        if (existingAsset) {
            console.log(`${logPrefix} Updating existing asset`);
            existingAsset.updateLocal(patch);
            await existingAsset.save();
            existingAsset.setLoading(true);
            uploadedAsset = existingAsset;
        } else {
            console.log(`${logPrefix} Creating new asset`);
            const schema = new AssetSchema();
            uploadedAsset = await this.addLocal({
                ...schema,
                ...patch,
                author: root.user.id
            });
            await uploadedAsset.save();
            uploadedAsset.setLoading(true);
        }

        await FirebaseFunctions.newAsset(
            creative,
            uploadedAsset.id,
            postProcess
        );
        this.uploadState = UploadStates.UPLOAD_COMPLETE;

        console.log(`${logPrefix} Complete`);
        return uploadedAsset;
    };

    newFolder = async (folderPath: string) => {
        const pathParts = folderPath.split('/').filter(Boolean);

        for (const [i, ancestorLeafName] of pathParts.entries()) {
            const ancestorPath = pathParts.slice(0, i + 1).join('/');
            const existingFolder = this.getByPath(ancestorPath);

            if (!existingFolder) {
                await this.add({
                    type: AssetFileType.FOLDER,
                    fileType: AssetFileType.FOLDER,
                    name: ancestorLeafName,
                    path: ancestorPath,
                    status: ProcessingStatus.COMPLETE,
                    dateModified: nowTimestamp(),
                    author: root.user.id
                });
            }
        }
    }

    // TODO: call a remove firebase function.

    getByStoragePath = (storage: string) => {
        for (const item of this.all) {
            if (item.data.storage === storage) return item;
        }
    }

    getByPath = (path: string) => {
        for (const item of this.all) {
            if (item.data.path === path) return item;
        }
    }

    getHostedURL = (id: string) => {
        const asset = this.getById(id);
        if (!asset) return '';
        const url = new URL(asset.data.storage, this.baseURL);
        return String(url);
    }

    getByNameInFolder = (name: string) => {
        const assets = this.folderContext;

        for (const asset of assets) {
            if (asset.data.name === name) return asset;
        }
    }

    removeFolder = async (path: string) => {
        const assets = this.getItemsRecursivelyInFolder(path);
        for (const asset of assets) {
            await asset.delete();
        }
    }

    remove = async (id: string) => {
        const asset = this.getById(id);
        if(!asset) {
            console.log('Asset not found:' + id);
            return;
        }

        await asset.delete();        
        return id;
    }

    reset = () => {

    }

    setSortOption(option: AssetSortOptions, direction: 'asc' | 'desc' = 'desc') {
        this.currentSortOption = option;
        this.sortDirection = direction;
    }
}

export default AssetsManager;
